// ============================================================================
// ACCOUNTS (Chart of Accounts) HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import {
  accountTypeService,
  accountSubtypeService,
  coaGroupService,
  coaLedgerService,
  voucherTypeService,
  journalEntryService,
  periodLockService,
} from 'api/services';
import type {
  PaginationMeta,
  ID,
  AccountTypeDto,
  AccountTypeQueryParams,
  AccountSubtypeDto,
  AccountSubtypeQueryParams,
  CoaGroupDto,
  CoaGroupQueryParams,
  CreateCoaGroupRequest,
  UpdateCoaGroupRequest,
  CoaLedgerDto,
  CoaLedgerQueryParams,
  CreateCoaLedgerRequest,
  UpdateCoaLedgerRequest,
  VoucherTypeDto,
  VoucherTypeQueryParams,
  CreateVoucherTypeRequest,
  UpdateVoucherTypeRequest,
  TransactionDto,
  JournalEntryQueryParams,
  PeriodLockDto,
  PeriodLockQueryParams,
} from 'api/types';

const DEFAULT_PAGINATION: PaginationMeta = {
  currentPage: 1,
  pageSize: 25,
  totalPages: 0,
  totalItems: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

// Generic list-hook factory (server-side pagination/filtering).
function useList<TDto, TParams>(
  fetcher: (params?: TParams) => Promise<{ data: TDto[]; pagination: PaginationMeta }>,
  params?: TParams,
) {
  const [data, setData] = useState<TDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState<PaginationMeta>(DEFAULT_PAGINATION);

  const paramsKey = JSON.stringify(params);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetcher(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, pagination, refetch: fetchData };
}

// ---- Read lookups ----
export const useAccountTypes = (params?: AccountTypeQueryParams) =>
  useList<AccountTypeDto, AccountTypeQueryParams>((p) => accountTypeService.getAll(p), params);

export const useAccountSubtypes = (params?: AccountSubtypeQueryParams) =>
  useList<AccountSubtypeDto, AccountSubtypeQueryParams>((p) => accountSubtypeService.getAll(p), params);

// ---- COA Group ----
export const useCoaGroups = (params?: CoaGroupQueryParams) =>
  useList<CoaGroupDto, CoaGroupQueryParams>((p) => coaGroupService.getAll(p), params);

export const useCoaGroupMutations = () => {
  const [loading, setLoading] = useState(false);

  const create = useCallback(async (data: CreateCoaGroupRequest) => {
    setLoading(true);
    try { return (await coaGroupService.create(data)).data; } finally { setLoading(false); }
  }, []);

  const update = useCallback(async (id: ID, data: UpdateCoaGroupRequest) => {
    setLoading(true);
    try { return (await coaGroupService.update(id, data)).data; } finally { setLoading(false); }
  }, []);

  const remove = useCallback(async (id: ID, rowVersion: number) => {
    setLoading(true);
    try { await coaGroupService.delete(id, rowVersion); } finally { setLoading(false); }
  }, []);

  const isCodeAvailable = useCallback(
    (code: string, excludeId?: ID) => coaGroupService.isCodeAvailable(code, excludeId), []);

  return { create, update, remove, isCodeAvailable, loading };
};

// ---- COA Ledger ----
export const useCoaLedgers = (params?: CoaLedgerQueryParams) =>
  useList<CoaLedgerDto, CoaLedgerQueryParams>((p) => coaLedgerService.getAll(p), params);

export const useCoaLedgerMutations = () => {
  const [loading, setLoading] = useState(false);

  const getById = useCallback(async (id: ID) => (await coaLedgerService.getById(id)).data, []);

  const create = useCallback(async (data: CreateCoaLedgerRequest) => {
    setLoading(true);
    try { return (await coaLedgerService.create(data)).data; } finally { setLoading(false); }
  }, []);

  const update = useCallback(async (id: ID, data: UpdateCoaLedgerRequest) => {
    setLoading(true);
    try { return (await coaLedgerService.update(id, data)).data; } finally { setLoading(false); }
  }, []);

  const remove = useCallback(async (id: ID, rowVersion: number) => {
    setLoading(true);
    try { await coaLedgerService.delete(id, rowVersion); } finally { setLoading(false); }
  }, []);

  const isCodeAvailable = useCallback(
    (code: string, excludeId?: ID) => coaLedgerService.isCodeAvailable(code, excludeId), []);

  return { getById, create, update, remove, isCodeAvailable, loading };
};

// ---- Voucher Type ----
export const useVoucherTypes = (params?: VoucherTypeQueryParams) =>
  useList<VoucherTypeDto, VoucherTypeQueryParams>((p) => voucherTypeService.getAll(p), params);

// ---- Journal Entry / vouchers (posting engine) ----
export const useJournalEntries = (params?: JournalEntryQueryParams) =>
  useList<TransactionDto, JournalEntryQueryParams>((p) => journalEntryService.getAll(p), params);

// ---- Period locks (ADR-0016) ----
export const usePeriodLocks = (params?: PeriodLockQueryParams) =>
  useList<PeriodLockDto, PeriodLockQueryParams>((p) => periodLockService.getAll(p), params);

export const useVoucherTypeMutations = () => {
  const [loading, setLoading] = useState(false);

  const getById = useCallback(async (id: ID) => (await voucherTypeService.getById(id)).data, []);

  const create = useCallback(async (data: CreateVoucherTypeRequest) => {
    setLoading(true);
    try { return (await voucherTypeService.create(data)).data; } finally { setLoading(false); }
  }, []);

  const update = useCallback(async (id: ID, data: UpdateVoucherTypeRequest) => {
    setLoading(true);
    try { return (await voucherTypeService.update(id, data)).data; } finally { setLoading(false); }
  }, []);

  const remove = useCallback(async (id: ID, rowVersion: number) => {
    setLoading(true);
    try { await voucherTypeService.delete(id, rowVersion); } finally { setLoading(false); }
  }, []);

  const isCodeAvailable = useCallback(
    (code: string, excludeId?: ID) => voucherTypeService.isCodeAvailable(code, excludeId), []);

  return { getById, create, update, remove, isCodeAvailable, loading };
};
